import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, LogIn, User } from "lucide-react";
import { SITE } from "@/data/site";
import { useClientAuth } from "@/context/ClientAuth";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Products", to: "/products" },
  { label: "Blog", to: "/blog" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { user, login } = useClientAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-brand-border py-3"
          : "bg-transparent border-b border-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src={SITE.logo} alt="Arivu Finserve" className="h-11 w-11 rounded-md object-contain" />
          <span className="hidden sm:flex flex-col leading-none">
            <span className={`font-serif text-xl font-semibold tracking-tight transition-colors ${scrolled ? "text-brand-navy" : "text-white"}`}>Arivu Finserve</span>
            <span className={`text-[10px] uppercase tracking-[0.25em] font-medium transition-colors ${scrolled ? "text-brand-green" : "text-brand-accent"}`}>Financial Planning</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`relative px-5 py-2 text-sm font-medium transition-colors ${
                scrolled
                  ? pathname === l.to ? "text-brand-navy" : "text-brand-muted hover:text-brand-navy"
                  : pathname === l.to ? "text-white" : "text-white/70 hover:text-white"
              }`}
            >
              {l.label}
              {pathname === l.to && (
                <motion.span
                  layoutId="nav-underline"
                  className="absolute left-5 right-5 -bottom-0.5 h-[2px] bg-brand-green rounded-full"
                />
              )}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <Link
              to="/account"
              data-testid="nav-account-btn"
              className={`inline-flex items-center gap-2 rounded-full text-sm font-medium px-4 py-2 transition-colors ${
                scrolled ? "text-brand-navy hover:bg-brand-bg" : "text-white hover:bg-white/10"
              }`}
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
              ) : (
                <User className="h-4 w-4" />
              )}
              <span className="max-w-[120px] truncate">{user.name?.split(" ")[0] || "Account"}</span>
            </Link>
          ) : (
            <button
              type="button"
              onClick={login}
              data-testid="nav-google-signin"
              className={`inline-flex items-center gap-2 rounded-full text-sm font-medium px-5 py-2.5 border transition-colors ${
                scrolled
                  ? "border-brand-border text-brand-navy hover:bg-brand-bg"
                  : "border-white/30 text-white hover:bg-white/10"
              }`}
            >
              <LogIn className="h-4 w-4" /> Sign in
            </button>
          )}

          <a
            href={SITE.calendly}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-book-btn"
            className={`inline-flex items-center gap-2 rounded-full text-sm font-medium px-6 py-3 transition-colors duration-300 group ${
              scrolled ? "bg-brand-navy text-white hover:bg-brand-green" : "bg-white text-brand-navy hover:bg-brand-accent hover:text-white"
            }`}
          >
            Book a Call
            <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`md:hidden p-2 transition-colors ${scrolled || open ? "text-brand-navy" : "text-white"}`}
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-white border-t border-brand-border"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className="py-3 text-lg font-serif text-brand-navy border-b border-brand-border/60"
                >
                  {l.label}
                </Link>
              ))}
              <a
                href={SITE.calendly}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-book-btn"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-brand-navy text-white text-sm font-medium px-6 py-3"
              >
                Book a Call <ArrowUpRight className="h-4 w-4" />
              </a>
              {user ? (
                <Link
                  to="/account"
                  data-testid="mobile-account-btn"
                  className="mt-2 inline-flex items-center justify-center gap-2 rounded-full border border-brand-border text-brand-navy text-sm font-medium px-6 py-3"
                >
                  <User className="h-4 w-4" /> My account
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={login}
                  data-testid="mobile-google-signin"
                  className="mt-2 inline-flex items-center justify-center gap-2 rounded-full border border-brand-border text-brand-navy text-sm font-medium px-6 py-3"
                >
                  <LogIn className="h-4 w-4" /> Sign in with Google
                </button>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
